// File: Login.js (updated)
import { db, auth } from "../services/firebase";
import {
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
} from "firebase/auth";

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    getDocs,
    query,
    where,
    collection,
    doc,
    setDoc,
    Timestamp,
    getDoc,
} from "firebase/firestore";
import Lottie from "react-lottie";
import "../App.css";

const Login = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [animationData, setAnimationData] = useState(null);
    const [info, setInfo] = useState("");

    useEffect(() => {
        fetch(`${process.env.PUBLIC_URL}/assets/quran02.json`)
            .then((response) => response.json())
            .then((data) => setAnimationData(data));
    }, []);

    const defaultOptions = {
        loop: true,
        autoplay: true,
        animationData: animationData,
        rendererSettings: {
            preserveAspectRatio: "xMidYMid slice",
        },
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");
        setInfo("");

        try {
            // 1) Auth: validate email + password
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const firebaseUser = userCredential.user;
            const userId = firebaseUser.uid;

            // 2) Ambil profile dari Firestore (users/{uid})
            const userRef = doc(db, "users", userId);
            const userSnap = await getDoc(userRef);

            if (!userSnap.exists()) {
                setError("Maklumat akaun tidak lengkap. Sila hubungi admin.");
                return;
            }

            const userData = userSnap.data();

            const isTeacher = userData.role === "teacher";
            const isStudent = userData.role === "student";
            let teacherData = null;
            let studentData = null;
            let schoolId = null;
            let classroomId = null;

            // ====== LOGIC GURU (sama macam sekarang, cuma tak check password Firestore) ======
            if (isTeacher) {
                if (!userData.approved) {
                    setError("Akaun anda belum diluluskan oleh admin sistem.");
                    return;
                }

                const teacherSnap = await getDocs(
                    query(collection(db, "teachers"), where("id", "==", userId))
                );

                if (teacherSnap.empty) {
                    let migratedSchools = userData.schools || [];
                    if (migratedSchools.length > 0) {
                        migratedSchools[0].isApproved = true;
                        migratedSchools[0].isAdmin = true;
                    }

                    const migratedData = {
                        id: userId,
                        name: userData.name,
                        email: userData.email,
                        createdAt: userData.createdAt || Timestamp.now(),
                        schools: migratedSchools,
                        isAdmin: true,
                    };

                    await setDoc(doc(db, "teachers", userId), migratedData);

                    // Trim users doc (tak perlu password)
                    await setDoc(
                        doc(db, "users", userId),
                        {
                            email: userData.email,
                            role: userData.role,
                            approved: true,
                        },
                        { merge: true }
                    );

                    teacherData = migratedData;
                } else {
                    teacherData = teacherSnap.docs[0].data();
                }

                const hasApprovedSchool = Array.isArray(teacherData.schools)
                    ? teacherData.schools.some((s) => s.isApproved)
                    : Object.values(teacherData.schools || {}).some((s) => s.isApproved);

                if (!hasApprovedSchool) {
                    setError("Akaun anda belum diluluskan oleh mana-mana sekolah.");
                    return;
                }
            }

            // ====== LOGIC MURID (sama macam sekarang) ======
            if (isStudent) {
                const studentRef = doc(db, "students", userId);
                const studentSnap = await getDoc(studentRef);

                if (!studentSnap.exists()) {
                    setError("Maklumat murid belum lengkap. Sila tunggu kelulusan guru.");
                    return;
                }

                studentData = studentSnap.data();

                schoolId = studentData.schoolId;
                const classroomApproval = (studentData.classroomApprovals || []).find(
                    (c) => c.isApproved
                );
                classroomId = classroomApproval?.classroomId;

                if (!schoolId || !classroomId) {
                    setError("Maklumat sekolah atau kelas tidak lengkap.");
                    return;
                }

                const classroomRef = doc(db, "schools", schoolId, "classrooms", classroomId);
                const classroomSnap = await getDoc(classroomRef);

                if (!classroomSnap.exists()) {
                    setError("Kelas tidak wujud.");
                    return;
                }

                const classroomData = classroomSnap.data();
                const isApproved = (classroomData.students || []).includes(userId);

                if (!isApproved) {
                    setError("Akaun anda belum diluluskan oleh guru.");
                    return;
                }
            }

            // ====== Build object untuk localStorage (tanpa password) ======
            const updatedUser = {
                id: userId,
                email: userData.email,
                role: userData.role,
                approved: true,
                name: isStudent
                    ? studentData.name
                    : userData.name || teacherData?.name || "Tanpa Nama",
                avatar: isStudent
                    ? studentData.avatar || null
                    : userData.avatar || teacherData?.avatar || null,
                isSuperAdmin: userData.isSuperAdmin || false,
                ...(isTeacher && teacherData && { schools: teacherData.schools || [] }),
                ...(isStudent && { schoolId, classroomId }),
            };

            localStorage.setItem("user", JSON.stringify(updatedUser));

            if (isTeacher) {
                window.location.href = "/teacher/dashboard";
            } else if (isStudent) {
                window.location.href = "/student/dashboard";
            } else {
                setError("Peranan pengguna tidak dikenali.");
            }
        } catch (err) {
            console.error("Login Error:", err);
            if (err.code === "auth/user-not-found" || err.code === "auth/wrong-password") {
                setError("Emel atau kata laluan tidak sah.");
            } else {
                setError("Masalah semasa log masuk.");
            }
        }
    };

    const handleForgotPassword = async () => {
        setError("");
        setInfo("");

        if (!email) {
            setError("Sila masukkan emel anda dahulu.");
            return;
        }

        try {
            await sendPasswordResetEmail(auth, email);
            // Untuk security, mesej generic sama walaupun email tak wujud
            setInfo("Jika emel wujud dalam sistem, pautan reset kata laluan telah dihantar.");
        } catch (err) {
            console.error("Reset password error:", err);
            setInfo("Jika emel wujud dalam sistem, pautan reset kata laluan telah dihantar.");
        }
    };



    return (
        <div className="page-container">
            <header className="appbar">
                <img
                    src={`${process.env.PUBLIC_URL}/assets/Tasmik-white-01.png`}
                    alt="Tasmik Logo"
                    className="appbar-logo"
                />
            </header>

            <main className="main-content">
                <div className="login-container">
                    {animationData && (
                        <Lottie options={defaultOptions} height={110} width={110} />
                    )}
                    <form onSubmit={handleLogin} className="login-form">
                        <div className="input-container">
                            <input
                                type="email"
                                id="email"
                                placeholder=" "
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="input-field"
                                required
                            />
                            <label htmlFor="email" className="floating-label">
                                Emel
                            </label>
                        </div>
                        <div className="input-container">
                            <input
                                type="password"
                                id="password"
                                placeholder=" "
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="input-field"
                                required
                            />
                            <label htmlFor="password" className="floating-label">
                                Katalaluan
                            </label>
                        </div>
                        <button type="submit" className="login-button">
                            Teruskan
                        </button>
                        <span
                            onClick={handleForgotPassword}
                            style={{
                                color: "#007bff",
                                cursor: "pointer",
                                marginTop: "10px",
                                display: "inline-block"
                            }}
                        >
                            Lupa kata laluan?
                        </span>



                        {info && <p style={{ color: "green" }}>{info}</p>}
                        {error && <p className="error-message">{error}</p>}

                    </form>

                    <p>
                        Tiada akaun? <Link to="/register" className="link">Daftar</Link>
                    </p>
                </div>
            </main>

            <footer className="footer-login">
                <p>Rekod Bacaan Tasmik</p>
                <p>tasmik.com © {new Date().getFullYear()}</p>
            </footer>
        </div>
    );
};

export default Login;
